import { useCallback, useEffect, useMemo, useState } from 'react'

import { AuthUserNode, MeNode, useAllUsersQuery } from '@/apollo/graphql'
import { IOption, IPageInfo, Maybe } from '@/types'
import { getFetchMoreStatus, userDisplayLabel } from '@/utils'

interface IUseSelect {
  initial?: Maybe<Partial<AuthUserNode | MeNode>>
}

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useSelect = ({ initial }: IUseSelect) => {
  const [users, setUsers] = useState<Partial<AuthUserNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)
  const [search, setSearch] = useState<string>('')

  const { loading, networkStatus } = useAllUsersQuery({
    fetchPolicy: 'network-only',
    variables: {
      offset: pageInfo?.offset || 0,
      first: pageSize,
      nameContains: search,
    },
    onCompleted: res => {
      if (res.allUsers?.edges) {
        const _users: Partial<AuthUserNode>[] = []
        res.allUsers.edges.forEach(e => {
          if (e?.node) _users.push(e.node)
        })

        setUsers(prev => [...prev, ..._users])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.allUsers?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  const userOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const opts = (users || []).map(s => {
      if (!exists && initial?.id === s.id) {
        exists = true
      }

      return {
        label: userDisplayLabel(s),
        value: s.id || '',
      }
    })
    if (!exists && initial && !search) {
      opts.unshift({
        label: userDisplayLabel(initial),
        value: initial.id || '',
      })
    }
    return opts
  }, [users, initial, search])

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: users.length }))
  }, [users?.length])

  const onSearch = useCallback(
    (text: string) => {
      if (search !== text) {
        setUsers([])
      }
      setSearch(text)
      setPageInfo(initialPageInfo)
    },
    [search],
  )

  return {
    loading,
    userOptions,
    pageInfo,
    onFetchMore,
    onSearch,
  }
}
